// client/src/hooks/useAuthReset.js
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * useAuthReset — privacy guard for user-scoped React state.
 *
 * Calls the supplied `reset` callback whenever the authenticated user
 * identity changes, including:
 *   • logout         (userId → null)
 *   • login          (null   → userId)   ← also fires so any pre-existing
 *                                          stale state is cleared before
 *                                          the new user's data is fetched
 *   • account swap   (idA    → idB)      ← shared-device scenario
 *   • forced 401     (auth:error event dispatched by services/api.js)
 *
 * This prevents the data-context cross-user leak documented in
 * P1-PRIVACY: when User A logged out and User B logged in on the same
 * tab without a full page reload, React state in data contexts (Account,
 * Budget, Transaction, etc.) still held User A's PII until each context
 * re-fetched. With this hook, every data-bearing context resets local
 * state on identity change.
 *
 * Usage:
 *   const reset = useCallback(() => {
 *     setAccounts([]);
 *     setError(null);
 *   }, []);
 *   useAuthReset(reset);
 */
const useAuthReset = (reset) => {
  const { user } = useAuth();
  const currentId = user?._id ?? user?.id ?? null;
  // Initialise to a sentinel that differs from any real id AND from null,
  // so the very first effect run does NOT trigger reset on initial mount
  // (we want to leave initial fetch logic to the host context).
  const prevIdRef = useRef(currentId);
  const firstRunRef = useRef(true);

  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      prevIdRef.current = currentId;
      return;
    }
    if (prevIdRef.current !== currentId) {
      prevIdRef.current = currentId;
      try {
        reset();
      } catch (e) {
        // never let a buggy reset crash the auth boundary
        // eslint-disable-next-line no-console
        console.warn('useAuthReset: reset() threw', e);
      }
    }
  }, [currentId, reset]);

  // Defensive: if the API layer forces a logout via 401 (services/api.js
  // dispatches `auth:error` after removing the token), clear immediately
  // even before AuthContext's `user` state catches up.
  useEffect(() => {
    const handler = () => {
      prevIdRef.current = null;
      try {
        reset();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('useAuthReset: reset() threw on auth:error', e);
      }
    };
    window.addEventListener('auth:error', handler);
    return () => window.removeEventListener('auth:error', handler);
  }, [reset]);
};

export default useAuthReset;
export { useAuthReset };
